import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, ShieldCheck } from "lucide-react";
import { DURATION_UNITS } from "@/lib/durations";
import ImageUploader from "@/components/ImageUploader";

const AMENITIES = ["Boat Lift", "Jet Ski Lift", "PWC Ramp", "Covered", "Enclosed", "Electricity", "Fresh Water", "Gated Access", "Parking", "Wi-Fi", "Storage Locker", "Kayak Rack", "24/7 Access"];

export default function ListDock() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [lakes, setLakes] = useState([]);
  const [restrictedList, setRestrictedList] = useState([]);
  const [suggestedAlts, setSuggestedAlts] = useState([]);
  const [form, setForm] = useState({
    title: "", lake: "", city: "", zip_code: "", description: "",
    max_boat_length_ft: 24, amenities: [],
    accommodates_boats: true,
    accommodates_pwc: false,
    images: [],
    // duration toggles + prices
    offer_daily: false, price_daily: "",
    offer_weekly: false, price_weekly: "",
    offer_monthly: true, price_per_month: "300",
    offer_yearly: false, price_yearly: "",
    // Compliance
    permit_compliance_confirmed: false,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);
  useEffect(() => { api.get("/lakes").then((r) => setLakes(r.data.lakes)); }, []);
  useEffect(() => {
    api.get("/waterways/restricted").then((r) => {
      setRestrictedList(r.data.restricted || []);
      setSuggestedAlts(r.data.suggested_alternatives || []);
    }).catch(() => {});
  }, []);

  const restrictedMatch = useMemo(() => {
    const q = (form.lake || "").trim().toLowerCase();
    if (!q) return null;
    return restrictedList.find((r) => {
      const n = r.name.toLowerCase();
      return n.includes(q) || q.includes(n);
    }) || null;
  }, [form.lake, restrictedList]);

  const toggleAmenity = (a) => setForm((f) => ({ ...f, amenities: f.amenities.includes(a) ? f.amenities.filter((x) => x !== a) : [...f.amenities, a] }));

  const submit = async (e) => {
    e.preventDefault();
    if (restrictedMatch) {
      toast.error(`${restrictedMatch.name} is managed by the ${restrictedMatch.authority} — SlipShare can't accept listings here.`);
      return;
    }
    if (!form.permit_compliance_confirmed) {
      toast.error("Please confirm your dock permit / HOA allows short-term guest arrangements.");
      return;
    }
    const chosen = DURATION_UNITS.filter((d) => form[`offer_${d.unit}`]);
    if (chosen.length === 0) { toast.error("Offer at least one rental duration"); return; }
    for (const d of chosen) {
      const price = parseFloat(form[d.price_field]);
      if (!price || price <= 0) { toast.error(`Set a valid ${d.labelSingular} price`); return; }
    }
    setSubmitting(true);
    try {
      if (!form.accommodates_boats && !form.accommodates_pwc) {
        toast.error("Select at least one vessel type (boats and/or PWCs)");
        setSubmitting(false); return;
      }
      const payload = {
        title: form.title,
        lake: form.lake,
        city: form.city,
        zip_code: form.zip_code,
        description: form.description,
        max_boat_length_ft: parseInt(form.max_boat_length_ft),
        accommodates_boats: form.accommodates_boats,
        accommodates_pwc: form.accommodates_pwc,
        amenities: form.amenities,
        images: form.images.map((i) => i.url).filter(Boolean),
        price_daily: form.offer_daily ? parseFloat(form.price_daily) : null,
        price_weekly: form.offer_weekly ? parseFloat(form.price_weekly) : null,
        price_per_month: form.offer_monthly ? parseFloat(form.price_per_month) : null,
        price_yearly: form.offer_yearly ? parseFloat(form.price_yearly) : null,
        permit_compliance_confirmed: form.permit_compliance_confirmed,
      };
      await api.post("/docks", payload);
      toast.success("Your dock is live!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="list-dock-page" className="container-editorial py-12 max-w-3xl">
      <div className="text-xs uppercase tracking-[0.3em] text-accent mb-3">Become a host</div>
      <h1 className="font-display text-4xl mb-2">List your dock</h1>
      <p className="text-muted-foreground mb-10">Tell boaters about your slip. Change details any time.</p>

      <form onSubmit={submit} className="space-y-6">
        <div>
          <Label>Listing title</Label>
          <Input data-testid="dock-title-input" required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="e.g. Sunset Cove — Covered Slip with Lift"/>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label>Waterway</Label>
            <Select value={form.lake} onValueChange={(v) => setForm({...form, lake: v})}>
              <SelectTrigger data-testid="dock-lake-select"><SelectValue placeholder="Choose a lake, bay, or coast"/></SelectTrigger>
              <SelectContent>{lakes.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>City</Label><Input data-testid="dock-city-input" required value={form.city} onChange={(e) => setForm({...form, city: e.target.value})}/></div>
          <div><Label>ZIP</Label><Input data-testid="dock-zip-input" required value={form.zip_code} onChange={(e) => setForm({...form, zip_code: e.target.value})}/></div>
        </div>

        {restrictedMatch && (
          <div
            data-testid="restricted-waterway-warning"
            className="rounded-2xl border border-red-300/70 bg-red-50/70 p-5"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-700 mt-0.5 shrink-0" strokeWidth={1.75}/>
              <div className="flex-1">
                <div className="font-medium text-red-900 mb-1">
                  {restrictedMatch.name} is managed by the {restrictedMatch.authority}.
                </div>
                <p className="text-sm text-red-900/80 mb-3">
                  Dock permits on this waterway typically prohibit short-term rentals. Listing here
                  could put your permit at risk, so <strong>SlipShare cannot accept listings on this lake.</strong>
                </p>
                <div className="text-xs uppercase tracking-widest text-red-900/70 mb-1.5">
                  Try one of these eligible waterways instead:
                </div>
                <div className="flex flex-wrap gap-2">
                  {suggestedAlts.slice(0, 5).map((alt) => (
                    <button
                      key={alt}
                      type="button"
                      data-testid={`suggest-lake-${alt.replace(/\s+/g, "-")}`}
                      onClick={() => setForm({...form, lake: alt})}
                      className="px-3 py-1 rounded-full bg-white border border-red-200 text-red-900 text-xs hover:border-red-400 hover:bg-red-50"
                    >
                      {alt}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        <div>
          <Label>Description</Label>
          <Textarea data-testid="dock-description-input" required rows={5} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Tell boaters about the water depth, access, neighborhood…"/>
        </div>

        <div>
          <Label className="text-base">Rental durations & pricing</Label>
          <p className="text-xs text-muted-foreground mb-3 mt-1">Check the durations you'd like to offer and set a price for each. At least one is required.</p>
          <div className="space-y-3">
            {DURATION_UNITS.map((d) => {
              const on = form[`offer_${d.unit}`];
              return (
                <div key={d.unit} data-testid={`duration-row-${d.unit}`} className={`rounded-xl border p-4 flex items-center gap-4 ${on ? "border-primary bg-primary/5" : "border-border"}`}>
                  <Checkbox
                    id={`offer-${d.unit}`}
                    data-testid={`offer-${d.unit}-toggle`}
                    checked={on}
                    onCheckedChange={(v) => setForm({...form, [`offer_${d.unit}`]: !!v})}
                  />
                  <label htmlFor={`offer-${d.unit}`} className="flex-1 capitalize cursor-pointer">
                    <span className="font-medium">{d.labelSingular}</span>
                    <span className="text-xs text-muted-foreground ml-2">Per {d.labelSingular} rental</span>
                  </label>
                  {on && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">$</span>
                      <Input
                        data-testid={`price-${d.unit}-input`}
                        type="number"
                        min={1}
                        step="0.01"
                        value={form[d.price_field]}
                        onChange={(e) => setForm({...form, [d.price_field]: e.target.value})}
                        className="w-32"
                        placeholder="0"
                      />
                      <span className="text-sm text-muted-foreground">{d.shortSuffix}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <Label className="text-base">What can dock here?</Label>
          <p className="text-xs text-muted-foreground mb-3 mt-1">Check every vessel type your dock can accommodate.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className={`cursor-pointer rounded-xl border p-4 flex items-center gap-3 ${form.accommodates_boats ? "border-primary bg-primary/5" : "border-border"}`}>
              <Checkbox
                data-testid="allow-boats-toggle"
                checked={form.accommodates_boats}
                onCheckedChange={(v) => setForm({...form, accommodates_boats: !!v})}
              />
              <div>
                <div className="font-medium">Boats</div>
                <div className="text-xs text-muted-foreground">Runabouts, pontoons, cruisers</div>
              </div>
            </label>
            <label className={`cursor-pointer rounded-xl border p-4 flex items-center gap-3 ${form.accommodates_pwc ? "border-primary bg-primary/5" : "border-border"}`}>
              <Checkbox
                data-testid="allow-pwc-toggle"
                checked={form.accommodates_pwc}
                onCheckedChange={(v) => setForm({...form, accommodates_pwc: !!v})}
              />
              <div>
                <div className="font-medium">Jet Skis / Wave Runners</div>
                <div className="text-xs text-muted-foreground">PWCs, Sea-Doos, personal watercraft</div>
              </div>
            </label>
          </div>
        </div>

        <div>
          <Label>Max boat length (ft)</Label>
          <Input data-testid="dock-length-input" required type="number" min={10} value={form.max_boat_length_ft} onChange={(e) => setForm({...form, max_boat_length_ft: parseInt(e.target.value)})}/>
        </div>
        <div>
          <Label>Amenities</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {AMENITIES.map((a) => (
              <button type="button" key={a} data-testid={`amenity-${a.replace(/\s+/g, "-")}`}
                onClick={() => toggleAmenity(a)}
                className={`px-3 py-1.5 rounded-full text-sm border ${form.amenities.includes(a) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`}>
                {a}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label>Photos</Label>
          <p className="text-xs text-muted-foreground mb-3 mt-1">Add up to 10 photos of your dock. First photo is the cover.</p>
          <ImageUploader
            images={form.images}
            onChange={(imgs) => setForm({...form, images: imgs})}
            maxImages={10}
          />
        </div>
        <div
          data-testid="permit-compliance-block"
          className={`rounded-2xl border p-5 ${form.permit_compliance_confirmed ? "border-accent/60 bg-accent/5" : "border-border"}`}
        >
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              data-testid="permit-compliance-checkbox"
              checked={form.permit_compliance_confirmed}
              onCheckedChange={(v) => setForm({...form, permit_compliance_confirmed: !!v})}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 font-medium">
                <ShieldCheck className="h-4 w-4 text-accent" strokeWidth={1.75}/>
                Permit &amp; compliance confirmation
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                I confirm that my dock permit, HOA, or lease allows short-term guest arrangements,
                and I take responsibility for compliance with any local rules. SlipShare is not liable
                for permit violations resulting from listings I create.
              </p>
            </div>
          </label>
        </div>
        <Button
          data-testid="publish-dock-btn"
          type="submit"
          disabled={submitting || !!restrictedMatch || !form.permit_compliance_confirmed}
          className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground px-8"
        >
          {submitting ? "Publishing…" : "Publish listing"}
        </Button>
      </form>
    </div>
  );
}
